import React from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { brand } from '../../theme/colors';

/**
 * DocumentHeader — shared branded header for all Eqaya documents.
 *
 * Props:
 *  documentType  — e.g. "PROPOSAL" | "MILESTONE REPORT" | "VERIFICATION REQUEST"
 *  issuer        — { name, city, logoUrl }
 *  date          — formatted date string
 *  referenceNumber — optional
 *  projectTitle    — optional
 *  meta          — optional object of extra key/value pairs shown in right column
 *
 * NOTE: email and phone are intentionally NOT displayed. All communication
 * must go through the Eqaya messaging system.
 */
const DocumentHeader = ({
  documentType = '',
  issuer = {},
  date,
  referenceNumber,
  projectTitle,
  meta,
}) => {
  return (
    <Box
      sx={{
        background: brand.primary,
        color: 'white',
        p: '28px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 4,
        borderRadius: 1,
      }}
    >
      {/* Left column: issuer info */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Avatar
          src={issuer.logoUrl}
          sx={{
            width: 64,
            height: 64,
            bgcolor: 'rgba(255,255,255,0.25)',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: 'white',
          }}
        >
          {issuer.name?.[0]?.toUpperCase() || 'E'}
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight="bold" sx={{ color: 'white', mb: 0.5 }}>
            {issuer.name || 'Eqaya'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            Contact via Eqaya messaging
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            {issuer.city || 'Zimbabwe'}
          </Typography>
        </Box>
      </Box>

      {/* Right column: document type watermark + date/ref/project */}
      <Box sx={{ textAlign: 'right' }}>
        <Typography
          sx={{
            fontSize: 48,
            fontWeight: 900,
            color: 'white',
            opacity: 0.1,
            lineHeight: 1,
            mb: 1,
            userSelect: 'none',
          }}
        >
          {documentType}
        </Typography>
        {date && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            {date}
          </Typography>
        )}
        {referenceNumber && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            Ref: {referenceNumber}
          </Typography>
        )}
        {projectTitle && (
          <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'white', mt: 0.5 }}>
            {projectTitle}
          </Typography>
        )}
        {meta &&
          Object.entries(meta).map(([key, value]) =>
            value ? (
              <Typography key={key} variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                {key}: {value}
              </Typography>
            ) : null
          )}
      </Box>
    </Box>
  );
};

export default DocumentHeader;
